import Image from 'next/image'
import RouteLink from 'next/link'
import React, { FC } from 'react'

import cx from 'classnames'
import { DateTime } from 'luxon'

import { Box, CardActionArea } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'

import Text from '~/components/shared/Text'

import Paths from '~/utils/paths'

import { BlogBaseArticle } from '~/models/Blog'

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      root: {
        borderRadius: '4px',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 0,
        '&::after': {
          content: "''",
          position: 'absolute',
          zIndex: 1,
          bottom: 0,
          left: 0,
          right: 0,
          height: '100%',
          width: '100%',
          background:
            'linear-gradient(180deg, rgba(35, 35, 35, 0) 38.44%, rgba(35, 35, 35, 0.8) 100%)',
          opacity: 0.5
        }
      },
      actionArea: {
        width: '100%',
        height: '100%',
        zIndex: 2
      },
      text: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        maxWidth: 215,
        zIndex: 2,
        marginRight: 10
      },
      title: {
        color: theme.palette.common.white
      },
      image: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        zIndex: -1
      },
      apectRatio: {
        '--aspect-ratio': '1/1.2',
        paddingTop: 'calc((1 / (var(--aspect-ratio))) * 100%)',

        '@supports (aspect-ratio: 1)': {
          aspectRatio: 'var(--aspect-ratio)',
          paddingTop: 'initial'
        }
      }
    }),
  { name: 'BlogPhotoCard' }
)

type Props = {
  data: BlogBaseArticle
  fullHeight?: boolean
  loading?: boolean
}

const BlogPhotoCard: FC<Props> = ({ data, loading }) => {
  const classes = useStyles()

  return (
    <Box
      className={cx(classes.root, classes.apectRatio)}
      width="100%"
      style={{ pointerEvents: loading ? 'none' : 'auto' }}
    >
      {loading ? (
        <Skeleton variant="rect" className={classes.image} />
      ) : (
        data.primary_image_url && (
          <Image
            alt={data.primary_image_alt}
            title={data.page_title}
            src={data.primary_image_url}
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            sizes="(max-width: 600px) 300px, 400px"
          />
        )
      )}
      <RouteLink
        href={Paths.newsReviews.buildArticle(data.id, data.content_name)}
        passHref
      >
        <CardActionArea className={classes.actionArea}>
          <Box className={classes.text}>
            <Text
              color="textSecondary"
              variant="h5"
              fontSize={14}
              fontFamily="Nunito Sans, sans-serif"
              className={classes.title}
              mb={1}
            >
              {loading ? (
                <Skeleton variant="rect" width={120} />
              ) : (
                DateTime.fromISO(data.publish_date).toFormat('EEE d MMM y')
              )}
            </Text>
            <Text
              color="textSecondary"
              variant="h5"
              fontSize={20}
              fontFamily="Prompt, sans-serif"
              className={classes.title}
            >
              {loading ? (
                <Skeleton variant="rect" width={180} />
              ) : (
                data.page_title
              )}
            </Text>
          </Box>
        </CardActionArea>
      </RouteLink>
    </Box>
  )
}

export default BlogPhotoCard
